import React from "react";
import Helmet from "react-helmet";
import Loadable from "react-loadable";

import {
  Page,
  PageContent,
  Section,
  SectionContent,
  SectionContentHeader,
  SectionRow,
  SectionColumn
} from "../components/Layout";
import { HeaderContainer } from "../components/HeaderContainer";
import { Footer } from "../components/Footer";
import { Button, ButtonGroup, TextButton } from "../components/Button";
import {
  PageHeadline,
  PageTagline,
  PageIntroduction,
  SectionHeadline,
  ColumnHeadline,
  SectionKicker,
  SectionIntroduction
} from "../components/Typography";
import { ApplicationWindows } from "../components/ApplicationWindow";
import { ComparisonFrame } from "../components/ComparisonFrame";
import { LinkCardContainer, LinkCard } from "../components/LinkCard";
import CatalogSpecimenPreview from "../components/CatalogSpecimenPreview";
import ColorSpecimenExample, {
  colorSpecimenExampleSource
} from "../components/specimen-examples/ColorSpecimenExample";
import ReactSpecimenExample, {
  reactSpecimenExampleSource
} from "../components/specimen-examples/ReactSpecimenExample";
import {
  FurtherReadingLinks,
  FurtherReadingLink
} from "../components/FurtherReadingLinks";
import { createLoadingComponent } from "../components/Loading";

import icCode from "../assets/images/icon/ic_code.svg";
import icEdit from "../assets/images/icon/ic_edit.svg";
import icExtensible from "../assets/images/icon/ic_extensible.svg";
import icNoLock from "../assets/images/icon/ic_nolock.svg";
import icPresentation from "../assets/images/icon/ic_presentation.svg";
import icVersatile from "../assets/images/icon/ic_versatile.svg";
import icBrowser from "../assets/images/icon/ic_browser.svg";
import icBusiness from "../assets/images/icon/ic_business.svg";
import icGitHub from "../assets/images/icon/ic_github.svg";
import Logo from "../assets/images/logo/catalog_logo.svg";
import CatalogBrand from "../assets/images/background/catalog_brand.png";
import CatalogWeb from "../assets/images/background/catalog_website.png";

import "./Home.css";

const ResponsivePlayground = Loadable({
  loader: () =>
    import("../components/ResponsivePlayground").then(m => m.default),
  loading: createLoadingComponent("Responsive example"),
  delay: 500
});

export const Home = () => (
  <Page>
    <Helmet title="Catalog" />
    <HeaderContainer transparent />
    <PageContent>
      <Section id="introduction">
        <SectionContent xs="txl-bm">
          <SectionContentHeader>
            <PageHeadline>
              <img src={Logo} className="pageHeadlineLogo" alt="Catalog" />
            </PageHeadline>
            <PageTagline>Living Style Guides for Digital Products</PageTagline>
            <PageIntroduction>
              With Catalog you combine design documentation with real, live
              components in one single place, making collaboration between
              designers and developers seamless.
            </PageIntroduction>
          </SectionContentHeader>
          <ButtonGroup>
            <Button type="primary" href="/try">
              Try Catalog
            </Button>
            <Button
              type="secondary"
              href="https://docs.catalog.style/"
              external
            >
              Documentation
            </Button>
          </ButtonGroup>
        </SectionContent>
        <SectionContent xs="tz-bl" l="tz-bz" bleed>
          <div className="windowSection">
            <ApplicationWindows />
          </div>
        </SectionContent>
      </Section>
      <Section id="features">
        <SectionContent xs="tl-bl" l="txl-bxl">
          <SectionContentHeader>
            <SectionKicker>Features</SectionKicker>
            <SectionHeadline>Why choose Catalog?</SectionHeadline>
            <SectionIntroduction>
              We believe that maintaining a style guide should require as little
              extra work as possible. That's why we built Catalog with a focus
              on simplicity: authoring feels natural for designers and
              integration is painless for developers.
            </SectionIntroduction>
          </SectionContentHeader>
          <div className="keypoints">
            <div className="sectionColumn">
              <img src={icEdit} alt="Edit" />
              <label>
                <ColumnHeadline>Simple Editing</ColumnHeadline>
                <p>
                  Catalog makes creating and maintaining a style guide as simple
                  as editing plain text files with your favorite editor. Using
                  Markdown formatting allows you to write gorgeous content using
                  an easy-to-learn syntax.
                </p>
              </label>
            </div>
            <div className="sectionColumn">
              <img src={icPresentation} alt="Presentation" />
              <label>
                <ColumnHeadline>Beautiful Presentation</ColumnHeadline>
                <p>
                  Catalog is designed from the ground up to put your design
                  system into the right light. The clean and simple design works
                  beautifully across devices and screen sizes.
                </p>
              </label>
            </div>
            <div className="sectionColumn">
              <img src={icCode} alt="Code" />
              <label>
                <ColumnHeadline>Direct Integration</ColumnHeadline>
                <p>
                  Catalog integrates right with React applications and displays
                  live components and code snippets in your style guide. So you
                  can stop the copy-and-paste madness.
                </p>
              </label>
            </div>
            <div className="sectionColumn">
              <img src={icNoLock} alt="No Lock-in" />
              <label>
                <ColumnHeadline>Zero Lock-in</ColumnHeadline>
                <p>
                  Catalog is open source and free to use, making it possible for
                  you to add, maintain, or replace it depending on your changing
                  requirements.
                </p>
              </label>
            </div>
            <div className="sectionColumn">
              <img src={icVersatile} alt="Versatile" />
              <label>
                <ColumnHeadline>Versatile</ColumnHeadline>
                <p>
                  Catalog does not enforce any specific taxonomy or structure:
                  Build upon established principles, like Atomic Design, or run
                  your very own pattern language.
                </p>
              </label>
            </div>
            <div className="sectionColumn">
              <img src={icExtensible} alt="Extensible" />
              <label>
                <ColumnHeadline>Extensible & Customizable</ColumnHeadline>
                <p>
                  Catalog is built using a modular architecture. It can be
                  extended with your own plugins and customized to fit your
                  needs.
                </p>
              </label>
            </div>
          </div>
        </SectionContent>
      </Section>
      <Section id="specimens">
        <SectionContent xs="tl-bz" m="txl-bxl">
          <SectionContentHeader>
            <SectionKicker>Specimens</SectionKicker>
            <SectionHeadline>What's included in Catalog?</SectionHeadline>
            <SectionIntroduction>
              Catalog offers a broad variety of building blocks – called
              Specimens – to document and specify every piece of your design
              system in the right format. Specimens are defined as Markdown code
              blocks.
            </SectionIntroduction>
          </SectionContentHeader>
          <SectionRow xs="txs-bs" m="tm-bxl" reverse>
            <SectionColumn>
              <ColumnHeadline>Design Guidelines</ColumnHeadline>
              <p>
                To specify design guidelines, Catalog comes with a set of basic
                Specimens for colors, typography, and imagery. More advanced
                Specimens including audio and video allow you to document
                audible or behavioral aspects of your interface like sound
                effects, animations, or transitions.
              </p>
              <p>
                <TextButton
                  href="https://docs.catalog.style/specimens"
                  external
                >
                  Explore all Specimens
                </TextButton>
              </p>
            </SectionColumn>
            <SectionColumn>
              <ComparisonFrame headline="Write Markdown, create design specification.">
                <CatalogSpecimenPreview source={colorSpecimenExampleSource}>
                  <ColorSpecimenExample />
                </CatalogSpecimenPreview>
              </ComparisonFrame>
            </SectionColumn>
          </SectionRow>
          <SectionRow xs="ts-bs" m="txl-bxl">
            <SectionColumn>
              <ColumnHeadline>Interactive Components</ColumnHeadline>
              <p>
                Document interactive, code-based components built with HTML, CSS
                and JavaScript. The rendered example also shows the underlying
                source code. If you integrate React components, they even have
                access to local state.
              </p>
              <p>
                <TextButton
                  href="https://docs.catalog.style/specimens/react"
                  external
                >
                  Learn about component integration
                </TextButton>
              </p>
            </SectionColumn>
            <SectionColumn>
              <ComparisonFrame headline="Integrate live components using React.">
                <CatalogSpecimenPreview source={reactSpecimenExampleSource}>
                  <ReactSpecimenExample />
                </CatalogSpecimenPreview>
              </ComparisonFrame>
            </SectionColumn>
          </SectionRow>
          <SectionRow xs="ts-bs" m="txl-bxl" reverse>
            <SectionColumn>
              <ColumnHeadline>Responsive Views</ColumnHeadline>
              <p>
                To understand how your interface looks and works, it's important
                to see it on different screen sizes. That's why Catalog comes
                with a powerful responsive mode that displays your components
                for different breakpoints. You can choose between a tabbed
                version or place individual screen sizes next to each other.
              </p>
              <p>
                <TextButton
                  href="https://docs.catalog.style/specimens/react#display-all-defined-screen-sizes-1"
                  external
                >
                  See it in action
                </TextButton>
              </p>
            </SectionColumn>
            <SectionColumn>
              <ComparisonFrame headline="Test responsive behavior across screen sizes.">
                <div className="responsive-playground">
                  <ResponsivePlayground />
                </div>
              </ComparisonFrame>
            </SectionColumn>
          </SectionRow>
        </SectionContent>
        <SectionContent xs="ts-bl" m="tz-bxl">
          <SectionIntroduction>
            Discover Catalog's full capabilities in the documentation.
          </SectionIntroduction>
          <Button type="secondary" href="https://docs.catalog.style" external>
            Documentation
          </Button>
        </SectionContent>
      </Section>
      <Section id="examples">
        <SectionContent xs="tl-bl" l="txl-bl">
          <SectionKicker>Examples</SectionKicker>
          <SectionHeadline>Made with Catalog</SectionHeadline>
          <SectionIntroduction>
            We've built Catalog in response to our own need for a robust yet
            flexible system when building digital products. The following
            examples show Catalog in action serving a few different use cases.
            We're excited to see what you're building with it.
          </SectionIntroduction>
        </SectionContent>
        <SectionContent bleed>
          <LinkCardContainer>
            <LinkCard
              id="catalog-brand"
              img={CatalogBrand}
              href="https://brand.interactivethings.com"
              label="Brand Guidelines"
              description="Guidelines for corporate identity and brand experience."
            />
            <LinkCard
              id="catalog-web"
              img={CatalogWeb}
              href="https://www.interactivethings.com/styleguide"
              label="Website Guidelines"
              description="Guidelines for website interface design."
            />
          </LinkCardContainer>
        </SectionContent>
      </Section>
      <Section id="further">
        <SectionContent xs="tl-bz" l="txl-bz">
          <SectionContentHeader>
            <SectionKicker>Getting Started</SectionKicker>
            <SectionIntroduction>
              Start using Catalog today with the help of our guides and
              resources. We're here, should you need any support.
            </SectionIntroduction>
          </SectionContentHeader>
          <FurtherReadingLinks>
            <FurtherReadingLink
              href="https://docs.catalog.style"
              icon={icBrowser}
              heading="Documentation"
              text="Learn how to install, integrate, and use Catalog."
            />
            <FurtherReadingLink
              href="https://github.com/interactivethings/catalog/"
              icon={icGitHub}
              heading="Github"
              text="Catalog is open source and freely available on GitHub."
            />
            <FurtherReadingLink
              href="https://www.interactivethings.com"
              icon={icBusiness}
              heading="Partner"
              text="If you need a partner for digital product design, we're happy to hear from you."
            />
          </FurtherReadingLinks>
        </SectionContent>
      </Section>
    </PageContent>
    <Footer />
  </Page>
);



// WEBPACK FOOTER //
// ./src/pages/Home.js